// First-party, privacy-friendly analytics tracker. Server: POST /api/analytics/collect (server/src/routes/analytics.js).
// - Sends nothing when Do Not Track or Global Privacy Control is on, and never tracks /admin pages.
// - Visitor id (vid) is a random UUID in localStorage, not a fingerprint. Session id (sid) rotates after
//   30 minutes of inactivity.
// - First-touch attribution (UTMs, referrer host, landing path) is kept in localStorage and sent once on
//   register / Google sign-in as `attribution` (see getAttribution()).
// - No query strings, no search text, no form contents are ever sent. Titles of search pages are omitted.
// - Ad click ids (gclid, msclkid, fbclid, ttclid) are reduced to their type (`clickIdType`); the raw id is never sent.
// - Referral codes (`?ref=CODE`, see docs/GROWTH_CONTRACT.md) are *functional*, not analytics: they earn both people
//   bonus AI credits, so the code + timestamp are kept for 60 days even with Do Not Track / without consent
//   (localStorage `icd_ref` plus a first-party `icd_ref` cookie the API reads as a fallback). A new ?ref= wins.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ENDPOINT = `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/api/analytics/collect`;
const VID_KEY = 'icd_vid';
const SESSION_KEY = 'icd_sess';
const ATTR_KEY = 'icd_attr';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];
const CLICK_ID_TYPES = ['gclid', 'msclkid', 'fbclid', 'ttclid'];
const REF_KEY = 'icd_ref';
const REF_TTL_MS = 60 * 24 * 60 * 60 * 1000;
const REF_RE = /^[A-Za-z0-9]{4,16}$/;
// Pages whose <title> can contain what the user typed.
const NO_TITLE = /^\/(search|ai|account|settings|reset-password|code-sets|team)(\/|$)/;

function storage() {
  try {
    const s = window.localStorage;
    const k = '__icd_t';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

function readJson(key) {
  const s = storage();
  if (!s) return null;
  try {
    const v = JSON.parse(s.getItem(key) || 'null');
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(key, JSON.stringify(value));
  } catch {
    // quota / private mode: tracking is best effort
  }
}

function uuid() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function trackingDisabled() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
  if (navigator.doNotTrack === '1' || window.doNotTrack === '1' || navigator.msDoNotTrack === '1') return true;
  // Global Privacy Control is treated exactly like Do Not Track: send nothing.
  if (navigator.globalPrivacyControl === true) return true;
  if (navigator.webdriver) return true;
  return /^\/admin(\/|$)/.test(window.location.pathname);
}

function getVid() {
  const s = storage();
  let vid = s ? s.getItem(VID_KEY) : null;
  if (!vid || !/^[A-Za-z0-9-]{8,64}$/.test(vid)) {
    vid = uuid();
    if (s) {
      try {
        s.setItem(VID_KEY, vid);
      } catch {
        // ignore
      }
    }
  }
  return vid;
}

function externalReferrerHost() {
  try {
    if (!document.referrer) return null;
    const host = new URL(document.referrer).hostname.toLowerCase();
    return host && host !== window.location.hostname.toLowerCase() ? host : null;
  } catch {
    return null;
  }
}

function currentUtm() {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  for (const k of UTM_KEYS) {
    const v = params.get(`utm_${k}`);
    if (v) utm[k] = v.slice(0, 100);
  }
  if (!utm.source && params.get('gclid')) Object.assign(utm, { source: 'google', medium: 'cpc' });
  if (!utm.source && params.get('msclkid')) Object.assign(utm, { source: 'bing', medium: 'cpc' });
  if (!utm.source && params.get('fbclid')) utm.source = 'facebook';
  if (!utm.source && params.get('ttclid')) utm.source = 'tiktok';
  return Object.keys(utm).length ? utm : null;
}

/** Which ad click id is on the landing URL: 'gclid' | 'msclkid' | 'fbclid' | 'ttclid' | null. Never the id itself. */
function currentClickIdType() {
  const params = new URLSearchParams(window.location.search);
  return CLICK_ID_TYPES.find((k) => params.get(k)) || null;
}

/**
 * Current session: { sid, lastAt, referrer, utm }. A new session starts after 30 min of inactivity or when the
 * visitor arrives with new campaign parameters. The session keeps its landing referrer/UTMs for every event.
 */
function getSession() {
  const now = Date.now();
  const utm = currentUtm();
  const clickIdType = currentClickIdType();
  let sess = readJson(SESSION_KEY);
  const expired = !sess || !sess.sid || now - (Number(sess.lastAt) || 0) > SESSION_IDLE_MS;
  const newCampaign = utm && sess && JSON.stringify(utm) !== JSON.stringify(sess.utm || null);
  if (expired || newCampaign) {
    sess = { sid: uuid(), lastAt: now, referrer: externalReferrerHost(), utm, clickIdType };
  }
  sess.lastAt = now;
  writeJson(SESSION_KEY, sess);
  return sess;
}

function captureFirstTouch(sess) {
  if (readJson(ATTR_KEY)) return;
  const utm = sess.utm || {};
  writeJson(ATTR_KEY, {
    source: utm.source || undefined,
    medium: utm.medium || undefined,
    campaign: utm.campaign || undefined,
    clickIdType: sess.clickIdType || undefined,
    referrerHost: sess.referrer || undefined,
    landingPath: window.location.pathname.slice(0, 300),
    firstSeenAt: new Date().toISOString(),
  });
}

function send(payload) {
  const body = JSON.stringify(payload);
  try {
    // text/plain is CORS-safelisted, so no preflight; the server parses it as JSON.
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) return;
  } catch {
    // fall through to fetch
  }
  try {
    fetch(ENDPOINT, { method: 'POST', body, headers: { 'Content-Type': 'text/plain' }, credentials: 'include', keepalive: true }).catch(() => {});
  } catch {
    // ignore
  }
}

function basePayload(type, path = window.location.pathname) {
  const sess = getSession();
  captureFirstTouch(sess);
  return {
    type,
    path: String(path || '/').slice(0, 300),
    referrer: sess.referrer || undefined,
    utm: sess.utm || undefined,
    clickId: sess.clickIdType || undefined,
    vid: getVid(),
    sid: sess.sid,
    lang: (navigator.language || '').slice(0, 35) || undefined,
    screenW: window.screen && window.screen.width ? Math.round(window.screen.width) : undefined,
  };
}

let lastPageview = { path: null, at: 0 };

function trackPageview(path) {
  if (trackingDisabled() || /^\/admin(\/|$)/.test(path)) return;
  const now = Date.now();
  // React StrictMode runs effects twice in development; never count the same page twice within a second.
  if (lastPageview.path === path && now - lastPageview.at < 1000) return;
  lastPageview = { path, at: now };
  const payload = basePayload('pageview', path);
  if (!NO_TITLE.test(payload.path) && window.location.pathname === path) payload.title = (document.title || '').slice(0, 160) || undefined;
  send(payload);
}

/** Custom event, e.g. trackEvent('pricing_cta_click'). `props` are accepted for API symmetry but never sent. */
// eslint-disable-next-line no-unused-vars
export function trackEvent(name, props) {
  if (trackingDisabled() || !name || !/^[A-Za-z0-9_.:-]{1,64}$/.test(String(name))) return;
  send({ ...basePayload('event'), name: String(name) });
}

/** First-touch attribution saved in localStorage, or null (e.g. Do Not Track / storage blocked). */
export function getAttribution() {
  if (typeof window === 'undefined') return null;
  if (!readJson(ATTR_KEY) && !trackingDisabled()) captureFirstTouch(getSession());
  return readJson(ATTR_KEY);
}

function refFromUrl(search) {
  try {
    const v = (new URLSearchParams(search === undefined ? window.location.search : search).get('ref') || '').trim();
    return REF_RE.test(v) ? v.toUpperCase() : '';
  } catch {
    return '';
  }
}

/** Store `?ref=CODE` from the URL (latest link wins) for 60 days. Functional storage: runs without analytics consent. */
export function captureReferral(search) {
  if (typeof window === 'undefined') return;
  const code = refFromUrl(search);
  if (!code) return;
  writeJson(REF_KEY, { code, at: new Date().toISOString() });
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${REF_KEY}=${encodeURIComponent(code)}; Max-Age=${Math.floor(REF_TTL_MS / 1000)}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // cookies blocked: localStorage (and the request body) still carry it
  }
}

/** The referral code to send on sign-up: from the URL, else the stored one if under 60 days old, else ''. */
export function getReferralCode() {
  if (typeof window === 'undefined') return '';
  const fromUrl = refFromUrl();
  if (fromUrl) return fromUrl;
  const stored = readJson(REF_KEY);
  if (!stored || !REF_RE.test(String(stored.code || ''))) return '';
  const at = Date.parse(stored.at);
  if (!Number.isFinite(at) || Date.now() - at > REF_TTL_MS) return '';
  return String(stored.code).toUpperCase();
}

/** Sends a pageview on every route change. Called once in App.js. */
export function useAnalyticsPageviews() {
  const location = useLocation();
  const { pathname, search } = location;
  useEffect(() => {
    captureReferral(search);
  }, [search]);
  useEffect(() => {
    // Wait briefly so the new page can set its <title>. Not cancelled on fast navigation: every page counts.
    setTimeout(() => trackPageview(pathname), 300);
  }, [pathname]);
}
